import { LocalizationState } from '../types';
import { t } from '../i18n';
import { isStatsHudVisible } from '../services/statsHudService';

/** Enum values stay English (compared in code); only display translates. */
const STATE_KEYS: Record<LocalizationState, string> = {
  [LocalizationState.NotStarted]: 'state.notStarted',
  [LocalizationState.PermissionNeeded]: 'state.permission',
  [LocalizationState.Localizing]: 'state.localizing',
  [LocalizationState.Localized]: 'state.localized',
  [LocalizationState.TrackingWeak]: 'state.trackingWeak',
  [LocalizationState.LostTracking]: 'state.lostTracking',
  [LocalizationState.Relocalizing]: 'state.relocalizing',
  [LocalizationState.Error]: 'state.error',
};

/** Pill-shaped status indicator shown during the AR session. */
export class LocalizationStatusView {
  readonly el: HTMLDivElement;
  private label: HTMLSpanElement;
  private dot: HTMLSpanElement;
  private state: LocalizationState = LocalizationState.NotStarted;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'status-pill';
    this.dot = document.createElement('span');
    this.dot.className = 'status-dot';
    this.label = document.createElement('span');
    this.el.append(this.dot, this.label);
    this.set(LocalizationState.NotStarted);
  }

  set(state: LocalizationState, detail?: string): void {
    this.state = state;
    const label = t(STATE_KEYS[state]);
    this.label.textContent = detail ? `${label} — ${detail}` : label;
    this.el.dataset.state = stateClass(state);
    this.applyVisibility();
  }

  /**
   * 2026-07-18 user request: once localized, the green pill is redundant
   * clutter for most visitors — it should disappear on its own. It stays
   * (or comes back) while the 📶 VPS/FPS debug readout is on, since seeing
   * "Localized" next to the FPS/confidence numbers is the whole point
   * there. Every OTHER state (localizing, weak tracking, lost, error…)
   * always stays visible — those are exactly when a visitor needs to see
   * what's happening.
   * Called from set() on every state change, AND from main.ts's VPS/FPS
   * toggle callback so an already-localized pill hides/shows immediately
   * without waiting for the next localization event.
   */
  applyVisibility(): void {
    const hide = this.state === LocalizationState.Localized && !isStatsHudVisible();
    this.el.classList.toggle('hidden', hide);
  }
}

function stateClass(s: LocalizationState): string {
  switch (s) {
    case LocalizationState.Localized:
      return 'ok';
    case LocalizationState.Localizing:
    case LocalizationState.Relocalizing:
      return 'busy';
    case LocalizationState.TrackingWeak:
    case LocalizationState.LostTracking:
    case LocalizationState.PermissionNeeded:
      return 'warn';
    case LocalizationState.Error:
      return 'error';
    default:
      return 'idle';
  }
}
